/* np-guide-calc – the calculators in guides (snippets/np-guide-calc.liquid).
   The rules of thumb are the same as in the guide texts (docs/content/guides/), so the
   calculator never contradicts the article around it. */
if (!customElements.get('np-guide-calc')) {
	/* Same table as the guide "Fendere og fortøjning": [up to boat length m, fender diameter cm, line diameter mm] */
	const FENDER_TABLE = [
		[6, '10–12', '10'],
		[8, '12–16', '10–12'],
		[10, '16–20', '12–14'],
		[12, '20–24', '14–16'],
		[15, '24–30', '16–18']
	];
	const LEAD_DEPTH = 0.5; /* lead batteries: use about half */
	const LITHIUM_DEPTH = 0.8; /* LiFePO4: about 80 % */
	const PAINT_WASTE = 1.1; /* +10 % */

	const TEXT = {
		da: {
			battery: (wh, ah, volt, days, lead, li) =>
				`Forbrug: <strong>${wh} Wh ≈ ${ah} Ah pr. døgn</strong> ved ${volt} V. ` +
				`Til ${days} døgn uden opladning: blybatteri ca. <strong>${lead} Ah</strong> · LiFePO4 ca. <strong>${li} Ah</strong>.`,
			paint: (litres) => `Du skal bruge ca. <strong>${litres} liter</strong> bundmaling.`,
			fender: (cm, perSide, line) =>
				`Fendere: <strong>${cm} cm</strong> i diameter, mindst <strong>${perSide} på hver side</strong>. Fortøjningsliner: <strong>${line} mm</strong>.`,
			fenderLarge: 'Til både over 15 m: følg fender- og tovværksproducentens tabel.',
			empty: 'Udfyld felterne for at se resultatet.'
		},
		en: {
			battery: (wh, ah, volt, days, lead, li) =>
				`Use: <strong>${wh} Wh ≈ ${ah} Ah per day</strong> at ${volt} V. ` +
				`For ${days} ${days === 1 ? 'day' : 'days'} without charging: lead battery about <strong>${lead} Ah</strong> · LiFePO4 about <strong>${li} Ah</strong>.`,
			paint: (litres) => `You need about <strong>${litres} litres</strong> of antifouling.`,
			fender: (cm, perSide, line) =>
				`Fenders: <strong>${cm} cm</strong> diameter, at least <strong>${perSide} on each side</strong>. Mooring lines: <strong>${line} mm</strong>.`,
			fenderLarge: 'For boats over 15 m, follow the fender and rope maker\'s table.',
			empty: 'Fill in the fields to see the result.'
		}
	};

	class NpGuideCalc extends HTMLElement {
		connectedCallback() {
			this.lang = this.dataset.lang === 'en' ? 'en' : 'da';
			this.locale = this.lang === 'en' ? 'en-GB' : 'da-DK';
			this.t = TEXT[this.lang];
			this.result = this.querySelector('[data-result]');
			const firstRow = this.querySelector('[data-row]');
			this.rowTemplate = firstRow ? firstRow.cloneNode(true) : null;

			this.addEventListener('input', () => this.update());
			this.addEventListener('change', () => this.update());
			this.addEventListener('click', (event) => {
				if (event.target.closest('[data-add]')) this.addRow();
				const remove = event.target.closest('[data-remove]');
				if (remove) {
					remove.closest('[data-row]').remove();
					this.update();
				}
			});
			this.update();
		}

		number(name, scope = this) {
			const field = scope.querySelector(`[name="${name}"]`);
			const value = parseFloat(String(field ? field.value : '').replace(',', '.'));
			return Number.isFinite(value) && value > 0 ? value : 0;
		}

		format(value, decimals = 0) {
			return value.toLocaleString(this.locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
		}

		addRow() {
			if (!this.rowTemplate) return;
			const row = this.rowTemplate.cloneNode(true);
			row.querySelectorAll('input').forEach((input) => (input.value = ''));
			this.querySelector('[data-rows]').appendChild(row);
			row.querySelector('input').focus();
			this.update();
		}

		update() {
			if (!this.result) return;
			const html = this[this.dataset.calc] ? this[this.dataset.calc]() : '';
			this.result.innerHTML = html || this.t.empty;
		}

		batteri() {
			const wh = Array.from(this.querySelectorAll('[data-row]')).reduce(
				(sum, row) => sum + this.number('watt', row) * Math.min(this.number('hours', row), 24),
				0
			);
			const volt = this.number('volt') || 12;
			const days = Math.max(1, Math.round(this.number('days') || 1));
			if (!wh) return '';
			const ah = wh / volt;
			return this.t.battery(
				this.format(Math.round(wh)),
				this.format(Math.ceil(ah)),
				volt,
				days,
				this.format(Math.ceil((ah * days) / LEAD_DEPTH)),
				this.format(Math.ceil((ah * days) / LITHIUM_DEPTH))
			);
		}

		bundmaling() {
			const area = this.number('area');
			const coats = Math.max(1, Math.round(this.number('coats') || 1));
			const coverage = this.number('coverage');
			if (!area || !coverage) return '';
			return this.t.paint(this.format(Math.ceil(((area * coats) / coverage) * PAINT_WASTE * 10) / 10, 1));
		}

		fendere() {
			const length = this.number('length');
			if (!length) return '';
			let index = FENDER_TABLE.findIndex(([upTo]) => length <= upTo);
			if (index === -1) return this.t.fenderLarge;
			if (this.querySelector('[name="heavy"]')?.checked) index += 1; /* size up, as the guide says */
			if (index >= FENDER_TABLE.length) return this.t.fenderLarge;
			const perSide = length > 10 ? 4 : 3;
			return this.t.fender(FENDER_TABLE[index][1], perSide, FENDER_TABLE[index][2]);
		}
	}
	customElements.define('np-guide-calc', NpGuideCalc);
}
